// Auto-generated lightweight class for ModularBreakdownTask
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { ModularBreakdownTaskReadAPI, ModularBreakdownTaskWriteAPI, ModularBreakdownTaskReadAPISchema, ModularBreakdownTaskWriteAPISchema } from '../types/modularbreakdowntask';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, BelongsToWorkspace, CurrentTaskAttachedTo, HasThread, IslandHoldsPin, OperatesOn, SubTaskOf, TriggeredByToolCall } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { Task } from '../../tasks/api/task';
import { ToolCallPart } from '../../chat/api/toolcallpart';
import { Workspace } from '../../base/api/workspace';
import { Thread } from '../../chat/api/thread';
import { ScopedNode } from '../../shared/api/scopednode';
import { PDMProduct } from '../../pdm/api/pdmproduct';
import { IslandPin } from '../../rflp/api/islandpin';



export interface ModularBreakdownTask extends Partial<ModularBreakdownTaskReadAPI> {}
export class ModularBreakdownTask extends NeoNodeBase<ModularBreakdownTaskReadAPI, ModularBreakdownTaskWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "ModularBreakdownTask";
  static readonly readSchema = ModularBreakdownTaskReadAPISchema;
  static readonly writeSchema = ModularBreakdownTaskWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    sub_tasks: { target: "Task", cardinality: "many", isParent: false, relClass: "SubTaskOf", direction: "in" },
    triggered_by: { target: "ToolCallPart", cardinality: "one", isParent: false, relClass: "TriggeredByToolCall", direction: "out" },
    workspace: { target: "Workspace", cardinality: "one", isParent: true, relClass: "BelongsToWorkspace", direction: "out" },
    thread: { target: "Thread", cardinality: "one", isParent: false, relClass: "HasThread", direction: "out" },
    target_node: { target: "ScopedNode", cardinality: "one", isParent: false, relClass: "OperatesOn", direction: "out" },
    parent_task: { target: "Task", cardinality: "one", isParent: false, relClass: "SubTaskOf", direction: "out" },
    currently_attached_to: { target: "PDMProduct", cardinality: "one", isParent: false, relClass: "CurrentTaskAttachedTo", direction: "out" },
    island_pins: { target: "IslandPin", cardinality: "many", isParent: false, relClass: "IslandHoldsPin", direction: "out" },
  };

  protected get modelName(): string {
    return 'ModularBreakdownTask';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return ModularBreakdownTask.label;
  }


  constructor(data: Partial<ModularBreakdownTaskWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: ModularBreakdownTaskReadAPI, loadContext?: Record<string, any>): ModularBreakdownTask {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { workspace, thread, target_node, parent_task, currently_attached_to, security_scope, sub_tasks, triggered_by, island_pins, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = ModularBreakdownTaskReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, workspace: workspace, thread: thread, target_node: target_node, parent_task: parent_task, currently_attached_to: currently_attached_to, security_scope: security_scope, sub_tasks: sub_tasks, triggered_by: triggered_by, island_pins: island_pins };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return ModularBreakdownTask.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'workspace', 'Workspace', ModelRegistry);
    loadOneRelationship(processed, 'thread', 'Thread', ModelRegistry);
    loadOneRelationship(processed, 'target_node', 'ScopedNode', ModelRegistry);
    loadOneRelationship(processed, 'parent_task', 'Task', ModelRegistry);
    loadOneRelationship(processed, 'currently_attached_to', 'PDMProduct', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadManyRelationship(processed, 'sub_tasks', 'Task', ModelRegistry);
    loadOneRelationship(processed, 'triggered_by', 'ToolCallPart', ModelRegistry);
    loadManyRelationship(processed, 'island_pins', 'IslandPin', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new ModularBreakdownTask(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'sub_tasks' - Python ORM style (cached)
  get sub_tasks(): RelationshipManager<this, Task> {
    return this.getOrCreateRelationshipManager('sub_tasks', () => {
      return new RelationshipManager(this, 'sub_tasks', 'many', SubTaskOf as typeof Relationship);
    });
  }

  // Relationship manager for 'triggered_by' - Python ORM style (cached)
  get triggered_by(): RelationshipManager<this, ToolCallPart> {
    return this.getOrCreateRelationshipManager('triggered_by', () => {
      return new RelationshipManager(this, 'triggered_by', 'one', TriggeredByToolCall as typeof Relationship);
    });
  }

  // Relationship manager for 'workspace' - Python ORM style (cached)
  get workspace(): RelationshipManager<this, Workspace> {
    return this.getOrCreateRelationshipManager('workspace', () => {
      return new RelationshipManager(this, 'workspace', 'one', BelongsToWorkspace as typeof Relationship);
    });
  }

  // Relationship manager for 'thread' - Python ORM style (cached)
  get thread(): RelationshipManager<this, Thread> {
    return this.getOrCreateRelationshipManager('thread', () => {
      return new RelationshipManager(this, 'thread', 'one', HasThread as typeof Relationship);
    });
  }

  // Relationship manager for 'target_node' - Python ORM style (cached)
  get target_node(): RelationshipManager<this, ScopedNode> {
    return this.getOrCreateRelationshipManager('target_node', () => {
      return new RelationshipManager(this, 'target_node', 'one', OperatesOn as typeof Relationship);
    });
  }

  // Relationship manager for 'parent_task' - Python ORM style (cached)
  get parent_task(): RelationshipManager<this, Task> {
    return this.getOrCreateRelationshipManager('parent_task', () => {
      return new RelationshipManager(this, 'parent_task', 'one', SubTaskOf as typeof Relationship);
    });
  }

  // Relationship manager for 'currently_attached_to' - Python ORM style (cached)
  get currently_attached_to(): RelationshipManager<this, PDMProduct> {
    return this.getOrCreateRelationshipManager('currently_attached_to', () => {
      return new RelationshipManager(this, 'currently_attached_to', 'one', CurrentTaskAttachedTo as typeof Relationship);
    });
  }

  // Relationship manager for 'island_pins' - Python ORM style (cached)
  get island_pins(): RelationshipManager<this, IslandPin> {
    return this.getOrCreateRelationshipManager('island_pins', () => {
      return new RelationshipManager(this, 'island_pins', 'many', IslandHoldsPin as typeof Relationship);
    });
  }

}
